import React, { useEffect, useState } from "react";
import AuthService from "../service/AmplifyAuth";
import TrustdService from "../service/TrustdService";
import StringUtils from "../utils/StringUtils";
import ErrorUtils from "../utils/ErrorUtils";
import { findCountryCode, getCountryPhoneCodes } from "../utils/CountryCodes";
import { toE164Phone, toNationalPhone } from "../utils/PhoneUtils";
import { Button, Card, Field, Select } from "../components/UI";
import { useToast } from "../hooks";
import { useNavigate } from "../router";

export default function UserInfo() {
  const cachedAttrs = AuthService.getCachedUserAttributes?.() || null;
  const normalizeAttrsPhone = (attrs) => {
    if (!attrs) return attrs;
    const country = findCountryCode((attrs.country || "United States").toUpperCase());
    const code = country?.phoneCode || "1";
    return {
      ...attrs,
      phone: toNationalPhone(attrs.phone || attrs.phoneNumber, code),
    };
  };
  const [user, setUser] = useState(null);
  const [userForm, setUserForm] = useState(normalizeAttrsPhone(cachedAttrs));
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(() => {
    if (!cachedAttrs?.country) return null;
    return findCountryCode((cachedAttrs.country || "United States").toUpperCase());
  });
  const countryPhoneCodes = getCountryPhoneCodes();
  const addToast = useToast();
  const navigate = useNavigate();
  const resolveActivePhoneCode = () => selectedCountry?.phoneCode || "1";
  const normalizePhoneInput = (value) => toNationalPhone(value, resolveActivePhoneCode());

  useEffect(() => {
    async function load() {
      const authenticatedUser = await AuthService.currentAuthenticatedUser();
      const attrs = await AuthService.currentAuthenticatedUserAttributes();
      setUser(authenticatedUser);
      setUserForm(normalizeAttrsPhone(attrs));
      const country = findCountryCode((attrs?.country || "United States").toUpperCase());
      if (country) setSelectedCountry(country);
    }
    load();
  }, []);

  const update = (field, value) => {
    setUserForm((form) => ({ ...form, [field]: value }));
    setFieldErrors((errors) => {
      if (!errors[field] || StringUtils.isEmpty(value)) return errors;
      const nextErrors = { ...errors };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const updateCountry = (country) => {
    setSelectedCountry(country);
    setFieldErrors((errors) => {
      if (!errors.country || !country) return errors;
      const nextErrors = { ...errors };
      delete nextErrors.country;
      return nextErrors;
    });
  };

  const buildUserForm = () => {
    if (!selectedCountry) return { ...userForm, country: null, phoneNumber: null };
    return {
      ...userForm,
      country: selectedCountry.phoneCountry,
      phone: normalizePhoneInput(userForm.phone),
      phoneNumber: toE164Phone(userForm.phone, selectedCountry.phoneCode),
    };
  };

  const validate = (form) => {
    const errors = {};
    if (StringUtils.isEmpty(form.contactName)) errors.contactName = "Name is required";
    if (StringUtils.isEmpty(form.company)) errors.company = "Company is required";
    if (!selectedCountry) errors.country = "Country Code is required";
    if (StringUtils.isEmpty(form.phoneNumber)) errors.phone = "Phone is required";
    if (StringUtils.isEmpty(form.address)) errors.address = "Address is required";
    Object.values(errors).forEach((detail) => addToast({ severity: "error", summary: "Validation Error", detail, life: 5000 }));
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updateUser = async () => {
    if (isSaving) return;
    const form = buildUserForm();
    setUserForm(form);
    if (!validate(form)) return;
    try {
      setIsSaving(true);
      await AuthService.updateUserAttributes(user, form);
      await TrustdService.saveUserInfo({
        username: form.username,
        email: form.username,
        contactName: form.contactName,
        company: form.company,
        website: form.website,
        phoneNumber: form.phoneNumber,
        country: form.country,
        phone: form.phone,
        address: form.address,
        sendEmail: form.sendEmail,
        channel: form.channel,
      });
      addToast({ severity: "success", summary: "Success", detail: "User Info Update Successful", life: 5000 });
      navigate("/account");
    } catch (error) {
      addToast({ severity: "error", summary: "Error", detail: ErrorUtils.parseError(error), life: 10000 });
    } finally {
      setIsSaving(false);
    }
  };

  const onEnter = (event) => {
    if (event.key === "Enter") {
      updateUser();
    }
  };

  if (!userForm) return <div className="loading-container" />;

  return (
    <div className="account-page-modern account-form-page">
      <Card className="account-form-card account-form-card-wide">
        <div className="account-form-head">
          <p className="dashboard-section-kicker">Settings</p>
          <h2>Host Details</h2>
        </div>
        <div className="account-form-grid">
          <Field label="Email"><input className="form-control" value={userForm.email || ""} readOnly /></Field>
          <Field label="Name" required error={fieldErrors.contactName}><input className="form-control" placeholder="contact name" value={userForm.contactName || ""} autoComplete="name" onKeyDown={onEnter} onChange={(event) => update("contactName", event.target.value)} /></Field>
          <Field label="Company" required error={fieldErrors.company}><input className="form-control" placeholder='company (type "Self" if no company)' value={userForm.company || ""} autoComplete="organization" onKeyDown={onEnter} onChange={(event) => update("company", event.target.value)} /></Field>
          <Field label="Website"><input className="form-control" placeholder="website" value={userForm.website || ""} autoCapitalize="none" spellCheck="false" onKeyDown={onEnter} onChange={(event) => update("website", event.target.value)} /></Field>
          <Field label="Country Code" required error={fieldErrors.country}><Select value={selectedCountry} onChange={updateCountry} options={countryPhoneCodes} labelKey="label" valueKey="phoneCountry" placeholder="Select a Country Code" /></Field>
          <Field label="Phone" required error={fieldErrors.phone}><input className="form-control" placeholder="phone" value={userForm.phone || ""} autoComplete="tel-national" inputMode="tel" onKeyDown={onEnter} onChange={(event) => update("phone", normalizePhoneInput(event.target.value))} /></Field>
          <Field label="Address" required error={fieldErrors.address}><input className="form-control" placeholder="address" value={userForm.address || ""} autoComplete="street-address" onKeyDown={onEnter} onChange={(event) => update("address", event.target.value)} /></Field>
        </div>
        <div className="account-form-actions">
          <Button label="Cancel" className="btn-danger" disabled={isSaving} onClick={() => navigate("/account")} />
          <Button label={isSaving ? "Updating..." : "Update"} disabled={isSaving} onClick={updateUser} />
        </div>
      </Card>
    </div>
  );
}
